import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Plus, Pencil, Trash2, DollarSign, TrendingUp, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';

export default function ProjectsTab({ clientId, projets, onUpdate }) {
  const [showModal, setShowModal] = useState(false);
  const [editingProjet, setEditingProjet] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    type_produit: '',
    budget_estime: '',
    probabilite: 50,
    etape: 'Prospection',
    date_cloture_prevue: '',
    description: ''
  });

  const handleAdd = () => {
    setEditingProjet(null);
    setFormData({
      nom: '',
      type_produit: '',
      budget_estime: '',
      probabilite: 50,
      etape: 'Prospection',
      date_cloture_prevue: '',
      description: ''
    });
    setShowModal(true);
  };

  const handleEdit = (projet) => {
    setEditingProjet(projet);
    setFormData({
      ...projet,
      budget_estime: projet.budget_estime.toString()
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.nom || !formData.type_produit || !formData.budget_estime) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const payload = {
        ...formData,
        budget_estime: parseFloat(formData.budget_estime)
      };

      if (editingProjet) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/projets/${editingProjet.id}`,
          payload
        );
        toast.success('Projet modifié avec succès');
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/projets`,
          payload
        );
        toast.success('Projet créé avec succès');
      }
      setShowModal(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving projet:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (projetId) => {
    if (!window.confirm('Supprimer ce projet ?')) return;

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/projets/${projetId}`);
      toast.success('Projet supprimé');
      onUpdate();
    } catch (error) {
      console.error('Error deleting projet:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getEtapeBadge = (etape) => {
    const colors = {
      'Prospection': 'bg-yellow-100 text-yellow-700',
      'Négociation': 'bg-blue-100 text-blue-700',
      'Validation': 'bg-purple-100 text-purple-700',
      'Signé': 'bg-green-100 text-green-700',
      'Terminé': 'bg-gray-100 text-gray-700'
    };
    return colors[etape] || 'bg-gray-100 text-gray-700';
  };

  const getProbabiliteColor = (prob) => {
    if (prob >= 75) return 'text-green-600';
    if (prob >= 50) return 'text-blue-600';
    if (prob >= 25) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProbabiliteIcon = (prob) => {
    if (prob >= 75) return '🟢';
    if (prob >= 50) return '🟡';
    if (prob >= 25) return '🟠';
    return '🔴';
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Projets / Contrats</h3>
        <Button onClick={handleAdd} className="gap-2">
          <Plus className="h-4 w-4" />
          Nouveau Projet
        </Button>
      </div>

      {projets.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-gray-500">
            Aucun projet enregistré.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projets.map((projet) => (
            <Card key={projet.id} className="hover:shadow-xl transition-shadow border-2">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <Badge className={getEtapeBadge(projet.etape)}>
                    {projet.etape}
                  </Badge>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEdit(projet)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => handleDelete(projet.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <h4 className="font-bold text-lg mb-2">{projet.nom}</h4>
                <p className="text-sm text-gray-600 mb-4">{projet.type_produit}</p>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-semibold">
                      Budget: {projet.budget_estime.toLocaleString()} GNF
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-gray-400" />
                    <span className={`text-sm font-semibold ${getProbabiliteColor(projet.probabilite)}`}>
                      Probabilité: {projet.probabilite}% {getProbabiliteIcon(projet.probabilite)}
                    </span>
                  </div>

                  {projet.date_cloture_prevue && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">
                        Clôture prévue: {new Date(projet.date_cloture_prevue).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>

                {projet.description && (
                  <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
                    {projet.description}
                  </div>
                )}

                {/* Progress bar */}
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${projet.probabilite}%` }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal Ajout/Édition */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingProjet ? 'Modifier le projet' : 'Nouveau projet'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Nom du projet *</Label>
              <Input
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Ex: Panneaux LED Aéroport Conakry"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type de produit *</Label>
                <Select
                  value={formData.type_produit}
                  onValueChange={(val) => setFormData({ ...formData, type_produit: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LED Grand Format">LED Grand Format</SelectItem>
                    <SelectItem value="Panneau Déroulant">Panneau Déroulant</SelectItem>
                    <SelectItem value="Affichage Digital">Affichage Digital</SelectItem>
                    <SelectItem value="Spot TV">Spot TV</SelectItem>
                    <SelectItem value="Radio">Publicité Radio</SelectItem>
                    <SelectItem value="Campagne Web">Campagne Web</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Budget estimé (GNF) *</Label>
                <Input
                  type="number"
                  value={formData.budget_estime}
                  onChange={(e) => setFormData({ ...formData, budget_estime: e.target.value })}
                  placeholder="45000000"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Étape actuelle *</Label>
                <Select
                  value={formData.etape}
                  onValueChange={(val) => setFormData({ ...formData, etape: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Prospection">Prospection</SelectItem>
                    <SelectItem value="Négociation">Négociation</SelectItem>
                    <SelectItem value="Validation">Validation</SelectItem>
                    <SelectItem value="Signé">Signé</SelectItem>
                    <SelectItem value="Terminé">Terminé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Probabilité de signature (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.probabilite}
                  onChange={(e) => setFormData({ ...formData, probabilite: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div>
              <Label>Date de clôture prévue</Label>
              <Input
                type="date"
                value={formData.date_cloture_prevue}
                onChange={(e) => setFormData({ ...formData, date_cloture_prevue: e.target.value })}
              />
            </div>

            <div>
              <Label>Description (optionnel)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Détails du projet..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleSubmit}>
                {editingProjet ? 'Modifier' : 'Créer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
